import { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import { PANEL_STYLES } from '../styles/viewerStyles';

const MIN_WIDTH = 250;
const MAX_WIDTH = 500;

/**
 * Reusable tool panel container component.
 * Provides consistent styling and resize.
 */
function ToolPanel({
  title,
  onClose,
  children,
  width: initialWidth = 285,
  maxHeight,
  headerContent,
  contentPadding = 1.5,
  showHeader = true,
  noPaddingLeft = false,
}) {
  const [panelWidth, setPanelWidth] = useState(initialWidth);
  const resizeRef = useRef(null);

  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = panelWidth;

    const onMouseMove = (ev) => {
      const delta = ev.clientX - startX;
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth + delta));
      setPanelWidth(newWidth);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [panelWidth]);

  const containerSx = noPaddingLeft
    ? PANEL_STYLES.containerNoPadding(panelWidth)
    : PANEL_STYLES.container(panelWidth);

  return (
    <Box sx={{
      ...containerSx,
      animation: 'toolPanelSlideIn 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
      '@keyframes toolPanelSlideIn': {
        from: { opacity: 0, transform: 'translateX(-20px)' },
        to: { opacity: 1, transform: 'translateX(0)' },
      },
    }}>
      {showHeader && (
        <Box sx={PANEL_STYLES.header}>
          <Typography variant="subtitle1" sx={PANEL_STYLES.headerTitle}>
            {title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, ml: 'auto' }}>
            {headerContent}
            <IconButton
              onClick={onClose}
              sx={PANEL_STYLES.closeButton}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      )}

      {maxHeight ? (
        <Box sx={PANEL_STYLES.contentWithScroll(maxHeight)}>
          {children}
        </Box>
      ) : (
        <Box sx={PANEL_STYLES.content(contentPadding)}>
          {children}
        </Box>
      )}

      {/* Resize handle on right edge */}
      <Box
        ref={resizeRef}
        onMouseDown={handleResizeStart}
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: 4,
          height: '100%',
          cursor: 'ew-resize',
          zIndex: 10,
          '&:hover': {
            bgcolor: 'rgba(33,150,243,0.3)',
          },
        }}
      />
    </Box>
  );
}

ToolPanel.propTypes = {
  title: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node,
  width: PropTypes.number,
  maxHeight: PropTypes.string,
  headerContent: PropTypes.node,
  contentPadding: PropTypes.number,
  showHeader: PropTypes.bool,
  noPaddingLeft: PropTypes.bool,
};

export default ToolPanel;
